/**
 * `client/src/audio/latence.ts` — le retard entre « le moteur audio a rendu ce son » et « le son sort
 * du haut-parleur » ⟦C50 · A-169 §2⟧.
 *
 * ## Pourquoi ce module existe
 *
 * Un `AudioBufferSourceNode.start(quand)` planifie le son à l'heure `quand` de l'horloge du contexte
 * (`currentTime`). Cette horloge est celle du moteur, pas celle de l'oreille : entre les deux il y a la
 * latence interne de Web Audio (`baseLatency`, un paquet de rendu) ET celle de la sortie (`outputLatency`),
 * qui dépend du matériel : 10 à 30 ms sur les haut-parleurs d'un Mac, **150 à 300 ms sur un casque
 * Bluetooth**. Mesuré le 01/10/2026 sur le Mac de Matt, Chrome 154, AirPods Max : `baseLatency` 5,3 ms,
 * `outputLatency` 168 ms, soit 176 ms d'écart constant entre `currentTime` et le son entendu
 * (`scripts/montage-latence.cjs`). Les carrés du Montage, eux, suivent l'horloge d'affichage : sans
 * compensation, la musique était entendue 176 ms APRÈS que les carrés avaient franchi la ligne, soit
 * plus qu'une fenêtre de coupe entière au palier le plus doux.
 *
 * ## Ce que ça mesure, et comment
 *
 * `getOutputTimestamp().contextTime` est « la position du flux qui sort MAINTENANT du haut-parleur », dans
 * le repère de `currentTime` : `currentTime − contextTime` est donc exactement la latence de bout en bout du
 * moteur audio à cet instant (mesurée à 176,0 ms, = 5,33 + 168 + 2,67 ms de quantum de rendu). C'est la
 * mesure DIRECTE, préférée. Quand `getOutputTimestamp` manque (Safari) ou n'a pas encore de valeur (le flux
 * de sortie démarre : `contextTime` vaut 0 pendant le premier quart de seconde), on retombe sur la somme
 * `outputLatency + baseLatency` déclarée, puis sur 0.
 *
 * **Module PUR** : la « carte son » lui est passée en paramètre (`HorlogeAudio`), aucune API globale.
 */

/** Au-delà, la mesure est du bruit (un onglet gelé au retour d'un veille) : on ne compense pas à l'aveugle. */
export const LATENCE_MAX_MS = 500;

/** La part de `AudioContext` dont on a besoin — de quoi tester sans navigateur. */
export interface HorlogeAudio {
  readonly currentTime: number;
  readonly baseLatency?: number;
  readonly outputLatency?: number;
  getOutputTimestamp?(): { contextTime?: number; performanceTime?: number };
}

export interface MesureLatence {
  /** La latence à compenser, en ms (≥ 0, ≤ `LATENCE_MAX_MS`). */
  ms: number;
  /** D'où elle vient : la mesure directe, les latences déclarées, ou rien (0). */
  source: "mesure" | "declaree" | "aucune";
}

function fini(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** La latence de sortie de ce contexte, maintenant. Ne lève jamais. */
export function mesurerLatenceSortie(h: HorlogeAudio): MesureLatence {
  try {
    const o = h.getOutputTimestamp?.();
    if (o && fini(o.contextTime) && o.contextTime > 0 && fini(h.currentTime)) {
      const ms = (h.currentTime - o.contextTime) * 1000;
      if (ms >= 0 && ms <= LATENCE_MAX_MS) return { ms, source: "mesure" };
    }
  } catch {
    /* getOutputTimestamp peut lever sur un contexte fermé : on passe à la suite */
  }
  const declaree = ((fini(h.outputLatency) ? h.outputLatency : 0) + (fini(h.baseLatency) ? h.baseLatency : 0)) * 1000;
  if (declaree > 0) return { ms: Math.min(LATENCE_MAX_MS, declaree), source: "declaree" };
  return { ms: 0, source: "aucune" };
}
